import { normalizeViText } from "../vi-normalizer.js";

/**
 * 🔴 AI-SMOOTH-7 (@UC0433 · QĐ người dùng 03/11/2026 §3.1) — CÂU ĐỌC NHẮC NHÓM KHÁC. Làn đọc đọc dữ liệu của phạm vi lượt (nhóm đang mở trong chat,
 * hoặc các nhóm đọc được). Người hỏi nhắc tên MỘT NHÓM KHÁC của chính họ («quỹ đội bóng công ty còn bao nhiêu» khi đang ở «Lớp 2A») ⇒ LLM chỉ
 * thấy số của nhóm trong dữ liệu và rất dễ trả lời bằng số đó — sai nhóm, sai tiền. Hàm này nhận ra ca đó TẤT ĐỊNH (không LLM) để caller trả
 * câu cố định thay vì gọi LLM.
 *
 * Độ CHÍNH XÁC trước độ phủ (bắt oan = người dùng mất một câu trả lời đúng; bỏ sót thì còn luật 16 của prompt):
 *  · chỉ so với tên các nhóm CỦA NGƯỜI HỎI + nhãn có dữ liệu (tên nhóm · quỹ · đợt của lượt) — không đoán tên nhóm lạ;
 *  · so theo TỪ, đúng luật dấu của trợ lý (`isWord` — ai-doer): câu có dấu so có dấu («hỏi» ≠ «hội», «đợi» ≠ «đội»); chỉ từ người gõ KHÔNG dấu
 *    mới so dạng bỏ dấu (review goppy AI-SMOOTH-7 MEDIUM) — cùng luật cho tên («Đôi bạn» không mở đầu bằng «đội»);
 *  · tên khớp ở bất cứ đâu CHỈ KHI chính nó mở đầu bằng danh từ nhóm («Lớp 5B», «Đội bóng công ty», «CLB cầu lông») VÀ chữ người hỏi gõ ở chỗ
 *    danh từ đó là danh từ nhóm theo đúng luật dấu (so với dạng CÓ dấu của danh từ, không với chữ của tên — review goppy M1: «cho mình hỏi phụ
 *    huynh…» ≠ nhóm «Hoi phu huynh»); mọi tên khác (một hay nhiều từ — «Nhà mình», «Cầu lông») phải đứng NGAY SAU một danh từ nhóm trong câu
 *    (review goppy HIGH: «bé nhà mình đóng đủ chưa» ≠ nhóm «Nhà mình»). Chữ gõ không dấu: chỉ «nhom» / «clb» / «cau lac bo» được nhận là danh
 *    từ nhóm — «doi/hoi/lop» mơ hồ (đội/đợi/đòi · hội/hỏi · lớp/lốp) — ở CẢ hai chỗ; TRỪ khi từ ngay sau danh từ trong tên là một MÃ có chữ số
 *    VÀ chữ — riêng «lop» nhận cả số trần («lop 5b», «doi u23», «lop 1» — không ai «hỏi 5b»; review goppy vòng 4 M1' · vòng 5 L1: người gõ không dấu
 *    là nhóm repo cam kết bảo vệ);
 *  · bỏ qua tên chung chung / quá ngắn, và tên mà phần SAU danh từ nhóm rỗng hoặc toàn từ hỏi / đại từ («Nhóm mình», «Nhóm còn bao nhiêu
 *    tiền» — khớp gần như mọi câu; review security LOW-2 · goppy MEDIUM);
 *  · nhãn nằm TRỌN trong một nhãn dài hơn cũng được nhắc ⇒ tính nhãn DÀI («quỹ CLB cầu lông» của nhóm đang xem bao «CLB cầu lông»; «Lớp 2A năm cũ»
 *    bao «Lớp 2A»); trùng tên mà một bản có dữ liệu ⇒ coi như có dữ liệu. Còn lại, nhắc BẤT KỲ nhóm khác nào ⇒ trả nhóm đó (không có dữ liệu để so).
 *  · nhãn CÓ dữ liệu không bao giờ được trả về (chúng chỉ chặn bắt oan) ⇒ luôn quét đủ, không lọc, không trần (đã có trần ở `ANSWER_CONTEXT_LIMITS`);
 *    nhãn KHÔNG có dữ liệu chỉ quét `GROUP_NAME_REFS_MAX` cái đầu — phần thừa bị bỏ = nghiêng về KHÔNG chặn, caller ghi WARN (review security
 *    LOW-C; trần đếm cả nhãn có dữ liệu thì tắt lặng chốt ở nhóm nhiều quỹ/đợt — review goppy M2 · security MEDIUM-1).
 * Trả nhóm khác được nhắc (giữ `id` để caller dựng nút chọn nhóm), hoặc `null`. THUẦN.
 */

export interface GroupNameRef {
  readonly name: string;
  /** Nhãn có DỮ LIỆU trong lượt này (tên nhóm · quỹ · đợt đã đọc), không chỉ tên. */
  readonly inData: boolean;
  /** Id nhóm (vắng ở nhãn quỹ/đợt) — caller dựng nút chọn nhóm. */
  readonly id?: string;
}

/** Trần số nhãn KHÔNG có dữ liệu (nhóm khác của người hỏi) được quét trong một câu — theo thứ tự của caller. */
export const GROUP_NAME_REFS_MAX = 100;

/** Danh từ nhóm (có dấu). Câu KHÔNG dấu chỉ nhận các bản bỏ dấu không mơ hồ — `PLAIN_NOUNS`. */
const GROUP_NOUNS: readonly (readonly string[])[] = [["nhóm"], ["clb"], ["câu", "lạc", "bộ"], ["đội"], ["hội"], ["lớp"]];
const PLAIN_NOUNS: ReadonlySet<string> = new Set(["nhom", "clb", "cau lac bo"]);
/** Khoá (bỏ dấu) quá chung — nhắc tới chúng không đủ để nói "đó là nhóm khác". */
const GENERIC_NAME_KEYS: ReadonlySet<string> = new Set(["nhom", "quy", "lop", "clb", "doi", "hoi", "quy lop", "quy nhom", "quy chung", "nhom lop", "cong ty", "gia dinh", "ban be"]);
/**
 * Từ hỏi / từ thường / đại từ của câu đọc — tên gồm TOÀN những từ này khớp gần như mọi câu (review security LOW-2). So bằng ĐÚNG quan hệ khớp của câu
 * (`sameWord`): chữ của tên khớp được một từ hỏi trong câu thì nó LÀ từ hỏi — mục không dấu nuốt mọi bản có dấu («tụi» ~ «tui», «Thư» ~ «thu», «Đỏ»
 * ~ «do»: câu «nhóm thu bao nhiêu» khớp được tên «Nhóm Thư», nên tên đó phải bị bỏ qua — review vòng 6 goppy M1 · security MEDIUM); hai chữ CÙNG
 * có dấu thì so đúng chữ («cờ» ≠ «có» — «Câu lạc bộ cờ» là tên riêng, review security vòng 4 NIT).
 */
const QUESTION_WORDS: readonly string[] = [
  "còn", "bao", "nhiêu", "tiền", "ai", "chưa", "đóng", "đã", "có", "không", "gì", "nào", "mấy", "rồi", "thu", "chi", "quỹ", "nợ", "nó", "hết", "đủ",
  "thiếu", "số", "thế", "sao", "được", "như", "mình", "bạn", "tôi", "xem", "của", "này", "nay", "kia", "đó", "do", "và", "với", "cho", "đợt",
  "ta", "chúng", "chung", "em", "anh", "họ", "tui", "tao", "bọn",
];
const MIN_NAME_KEY_CHARS = 4;

/** Một từ: chữ gốc (NFC, hạ hoa) + dạng bỏ dấu (gập MỘT lần) + có gõ không dấu không. */
interface Word {
  readonly raw: string;
  readonly fold: string;
  readonly plain: boolean;
}

const wordOf = (raw: string): Word => ({ raw, fold: normalizeViText(raw), plain: /^[a-z0-9]+$/u.test(raw) });
const wordsOf = (s: string): Word[] =>
  s.normalize("NFC").toLowerCase().split(/[^\p{L}\p{N}]+/u).filter((t) => t.length > 0).map(wordOf);
const NOUN_WORDS: readonly (readonly Word[])[] = GROUP_NOUNS.map((noun) => noun.map(wordOf));
const QUESTION_WORD_LIST: readonly Word[] = QUESTION_WORDS.map(wordOf);

/** Luật dấu của trợ lý: đúng chữ; hoặc MỘT bên gõ không dấu và khớp dạng bỏ dấu của bên kia. */
function sameWord(a: Word | undefined, b: Word): boolean {
  if (a === undefined) return false;
  return a.raw === b.raw || ((a.plain || b.plain) && a.fold === b.fold);
}
const phraseAt = (q: readonly Word[], at: number, phrase: readonly Word[]): boolean => at >= 0 && phrase.every((w, k) => sameWord(q[at + k], w));
const isQuestionWord = (w: Word): boolean => QUESTION_WORD_LIST.some((qw) => sameWord(w, qw));

/** Câu có danh từ nhóm `noun` ở vị trí `at`; chữ gõ không dấu chỉ nhận danh từ không mơ hồ (`PLAIN_NOUNS`). */
function nounAt(q: readonly Word[], at: number, noun: readonly Word[]): boolean {
  if (!phraseAt(q, at, noun)) return false;
  return !q.slice(at, at + noun.length).every((w) => w.plain) || PLAIN_NOUNS.has(noun.map((w) => w.fold).join(" "));
}
/** Có danh từ nhóm KẾT THÚC ngay trước vị trí `end` của câu. */
const nounBefore = (q: readonly Word[], end: number): boolean => NOUN_WORDS.some((noun) => nounAt(q, end - noun.length, noun));

/**
 * Đầu tên `lead` (danh từ mơ hồ) gõ KHÔNG dấu trong câu, và từ ngay sau nó trong tên là MÃ: có chữ số VÀ chữ («Lớp 5B», «Đội U23»); riêng «lop»
 * nhận cả số trần («Lớp 1»…«Lớp 12» — «lốp» gần như không xuất hiện ở app quỹ). «doi 2 ngay» = «đợi 2 ngày», «hoi 1 cau» = «hỏi 1 câu» — không
 * phải nhóm «Đội 2» / «Hội 1» (review goppy vòng 5 L1 · security vòng 5 NIT).
 */
function plainNounBeforeCode(q: readonly Word[], at: number, lead: readonly Word[], name: readonly Word[]): boolean {
  const code = name[lead.length]?.raw ?? "";
  const isCode = /\p{N}/u.test(code) && (/\p{L}/u.test(code) || lead.map((w) => w.fold).join(" ") === "lop");
  return isCode && q.slice(at, at + lead.length).every((w) => w.plain);
}

/** Danh từ nhóm (dạng CÓ dấu) mở đầu `name`, theo luật dấu của trợ lý; `undefined` = không mở đầu bằng danh từ nhóm. */
const leadingNoun = (name: readonly Word[]): readonly Word[] | undefined => NOUN_WORDS.find((noun) => phraseAt(name, 0, noun));

/** Tên nhóm mở đầu bằng danh từ nhóm («Nhóm Chạy Bộ», «Đội bóng», «CLB …») — chỗ ghép câu dùng để không viết «nhóm «Nhóm …»». */
export function startsWithGroupNoun(name: string): boolean {
  return leadingNoun(wordsOf(name)) !== undefined;
}

/** Tên KHÔNG có dữ liệu bị bỏ qua: chung chung · quá ngắn · phần sau danh từ nhóm rỗng hoặc toàn từ hỏi / đại từ. */
function tooVague(words: readonly Word[], key: string, lead: readonly Word[] | undefined): boolean {
  const core = words.slice(lead?.length ?? 0);
  return key.length < MIN_NAME_KEY_CHARS || GENERIC_NAME_KEYS.has(key) || core.every(isQuestionWord);
}

interface Hit {
  readonly ref: GroupNameRef;
  readonly a: number;
  readonly b: number;
}

export function otherGroupMention(question: string, groups: readonly GroupNameRef[]): GroupNameRef | null {
  const q = wordsOf(question);
  // Một khoá một mục; trùng tên mà một bản có dữ liệu ⇒ coi như có dữ liệu (nghiêng về KHÔNG chặn).
  const byKey = new Map<string, { readonly ref: GroupNameRef; readonly words: readonly Word[]; readonly lead: readonly Word[] | undefined }>();
  const scanned = [...groups.filter((r) => r.inData), ...groups.filter((r) => !r.inData).slice(0, GROUP_NAME_REFS_MAX)];
  for (const ref of scanned) {
    const words = wordsOf(ref.name);
    const key = words.map((w) => w.fold).join(" ");
    const lead = leadingNoun(words);
    if (words.length === 0 || (!ref.inData && tooVague(words, key, lead))) continue;
    const seen = byKey.get(key);
    if (seen === undefined || (!seen.ref.inData && ref.inData)) byKey.set(key, { ref, words, lead });
  }
  const hits: Hit[] = [];
  for (const { ref, words, lead } of byKey.values()) {
    for (let i = 0; i + words.length <= q.length; i++) {
      if (!phraseAt(q, i, words)) continue;
      // Nhãn có dữ liệu: khớp lỏng (chỉ để chặn bắt oan). Nhóm khác: danh từ nhóm của CÂU ngay trước tên, hoặc ở chính đầu tên (`nounAt`).
      const headIsNoun = lead !== undefined && (nounAt(q, i, lead) || plainNounBeforeCode(q, i, lead, words));
      if (ref.inData || nounBefore(q, i) || headIsNoun) hits.push({ ref, a: i, b: i + words.length });
    }
  }
  // Nhãn nằm TRỌN trong một nhãn dài hơn cũng được nhắc ⇒ câu nói về nhãn dài.
  const kept = hits.filter((h) => !hits.some((g) => g !== h && g.a <= h.a && h.b <= g.b && g.b - g.a > h.b - h.a));
  return [...kept].sort((x, y) => x.a - y.a).find((h) => !h.ref.inData)?.ref ?? null;
}
